/**
 * Everything on file at this location, in one place.
 *
 * This tab replaces TWO surfaces that each showed part of the truth:
 *
 *   - the old "Site History" tab, which read only `/site-photos` --
 *     `site_attachments` alone, so nothing a tech ever uploaded on a job
 *     or against a device appeared here;
 *   - the per-trade "Attachments" section, which read only `/attachments`
 *     -- `job_attachments` alone, and only for one trade at a time.
 *
 * NEITHER read `asset_attachments`, so between them they could not produce
 * the union, and could not dedupe the backflow photo fanout (the same file
 * written to two tables). `GET /api/sites/:siteId/documents` does both,
 * reusing the loaders that have served the FIELD app since
 * TECH-JOB-SCREEN §3e -- the office was seeing LESS than the tech.
 *
 * Grouped by device, because "what's on file for THIS assembly" is the
 * question the office is actually answering when a customer calls about
 * one backflow. Files with no device land in a "Site documents" group.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { openExternalLink } from "@/lib/standalone";
import { AttachmentPreviewDialog } from "@/components/attachment-preview-dialog";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable, useSortedRows, type Column } from "@/components/ui/data-table";
import { formatDate } from "@/lib/utils";
import { Download, ExternalLink, FolderOpen, Paperclip } from "lucide-react";
import { sanitizeFilename, setDragOutData } from "@/lib/drag-out";
import { api, attachmentDownloadUrl } from "@/lib/api";
import { dateSortValue } from "@/lib/table-sort";
import { ShowMoreFooter } from "./section-chrome";
import { DEFAULT_VISIBLE_ROWS, type SiteContext } from "./types";

export interface SiteFileRow {
    id: string;
    kind: "photo" | "document";
    name: string;
    date: string;
    assetId: string | null;
    url: string;
    thumbUrl?: string;
    /** Office-only extras — absent on asset/site rows, which is a real "—". */
    category?: string | null;
    jobId?: string | null;
    jobTitle?: string | null;
    jobNumber?: string | null;
    driveFileId?: string | null;
    /** Photos only: `2" Double Check · SN 40613G · Tag photo` (server-built). */
    photoLabel?: string | null;
}

export interface SiteFileGroup {
    assetId: string | null;
    label: string;
    /** `assets.name` — the office's identifier. Falls back to `label`. */
    assetName?: string | null;
    totalCount: number;
    items: SiteFileRow[];
    nextCursor: string | null;
}

export function useSiteFiles(siteId: string) {
    return useQuery({
        queryKey: ["site-documents", siteId],
        queryFn: () => api.get<{ groups: SiteFileGroup[]; total: number }>(`/api/sites/${siteId}/documents`),
        enabled: !!siteId,
    });
}

export function DocumentsSection({ site }: { site: SiteContext }) {
    const q = useSiteFiles(site.siteId);
    const [expanded, setExpanded] = useState(false);
    const [preview, setPreview] = useState<SiteFileRow | null>(null);

    // One flat table across every group, newest first, with the device
    // named per row. Grouping is information, not navigation — a CSR
    // scanning for "the 2024 test report" should not have to open seven
    // accordions to find out it isn't there.
    const rows = useMemo(() => {
        const all = (q.data?.groups ?? []).flatMap((g) =>
            g.items.map((i) => ({ ...i, deviceLabel: g.assetId ? (g.assetName ?? g.label) : null })),
        );
        return all.sort((a, b) => (a.date === b.date ? (a.id < b.id ? 1 : -1) : a.date < b.date ? 1 : -1));
    }, [q.data]);

    const total = q.data?.total ?? rows.length;

    const columns: Column<(typeof rows)[number]>[] = [
        {
            key: "name",
            header: "File",
            sortable: true,
            render: (r) => (
                // Drag-out to Gmail / Finder / the desktop. This came off the
                // old Site History card and is the one thing on that card the
                // office uses daily — attaching a test report to a reply
                // without downloading it first. Carried over deliberately.
                <div
                    className="flex min-w-0 cursor-grab items-center gap-2 active:cursor-grabbing"
                    draggable
                    onDragStart={(e) =>
                        setDragOutData(e, {
                            url: attachmentDownloadUrl(r.id, { disposition: "attachment" }),
                            filename: sanitizeFilename(r.name),
                        })
                    }
                    title="Drag out to Gmail, Finder, or your desktop"
                >
                    <Paperclip className="h-3.5 w-3.5 shrink-0 text-foreground/80" />
                    <span className="truncate text-sm">{r.name}</span>
                </div>
            ),
        },
        {
            key: "category",
            header: "Type",
            sortable: true,
            sortAccessor: (r) => r.category ?? r.kind,
            render: (r) => (
                <Badge variant="outline" className="text-[10px] uppercase">
                    {r.category ?? r.kind}
                </Badge>
            ),
        },
        {
            key: "deviceLabel",
            header: "Device",
            sortable: true,
            sortAccessor: (r) => (r.assetId ? (r.deviceLabel ?? "Device") : "Site"),
            render: (r) =>
                r.assetId ? (
                    <Link to={`/assets/${r.assetId}`} className="text-xs hover:underline">
                        {r.deviceLabel ?? "Device"}
                    </Link>
                ) : (
                    <span className="text-xs text-foreground/80">Site</span>
                ),
        },
        {
            key: "jobTitle",
            header: "Job",
            sortable: true,
            sortAccessor: (r) => (r.jobId ? (r.jobNumber ?? r.jobTitle ?? "") : null),
            render: (r) =>
                r.jobId ? (
                    <Link to={`/jobs/${r.jobId}`} className="text-xs text-primary hover:underline">
                        {r.jobNumber ? `#${r.jobNumber}` : (r.jobTitle ?? "open")}
                    </Link>
                ) : (
                    <span className="text-xs text-foreground/80">—</span>
                ),
        },
        {
            key: "date",
            header: "Added",
            sortable: true,
            firstSortDir: "desc",
            sortAccessor: (r) => dateSortValue(r.date),
            render: (r) => <span className="text-xs tabular-nums text-foreground/80">{formatDate(r.date)}</span>,
        },
        {
            key: "open",
            header: "",
            render: (r) =>
                r.driveFileId ? (
                    <button
                        type="button"
                        onClick={() => openExternalLink(`https://drive.google.com/file/d/${r.driveFileId}/view`)}
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    >
                        <ExternalLink className="h-3 w-3" /> Drive
                    </button>
                ) : (
                    <span className="inline-flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setPreview(r)}
                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                        >
                            <ExternalLink className="h-3 w-3" /> Open
                        </button>
                        <a
                            href={attachmentDownloadUrl(r.id)}
                            className="rounded p-1 text-foreground/70 hover:bg-muted hover:text-foreground"
                            title="Download"
                        >
                            <Download className="h-3.5 w-3.5" />
                        </a>
                    </span>
                ),
        },
    ];

    // Sort every file, THEN cut to the preview, so a sort reaches past the
    // first few rows. Newest first until someone picks another column.
    const { sorted, controlledSort } = useSortedRows(rows, columns, { sortId: "site.documents" });
    const visible = expanded ? sorted : sorted.slice(0, DEFAULT_VISIBLE_ROWS);

    return (
        <Card>
            <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                    <FolderOpen className="h-4 w-4" /> Documents ({total})
                </CardTitle>
            </CardHeader>
            <CardContent>
                <DataTable
                    columns={columns}
                    data={visible}
                    keyField="id"
                    controlledSort={controlledSort}
                    loading={q.isLoading}
                    emptyMessage="Nothing on file at this location yet. Drop a file anywhere on this page to add one."
                />
                <ShowMoreFooter total={total} expanded={expanded} onToggle={() => setExpanded((v) => !v)} />
                {/* Structured test history begins at go-live — say so rather
                    than letting an empty older-years stretch read as "we never
                    worked here". */}
                <p className="mt-3 text-xs text-foreground/70">
                    Imported files carry their original upload date. Structured test history begins at go-live; older
                    work may exist only as a scanned report.
                </p>
            </CardContent>
            <AttachmentPreviewDialog
                open={!!preview}
                onClose={() => setPreview(null)}
                attachmentId={preview?.id ?? ""}
                fileName={preview?.name ?? "Attachment"}
            />
        </Card>
    );
}
